import {Injectable} from '@angular/core';
import {formatDate} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {ToastController} from '@ionic/angular';
import addMinutes from 'date-fns/addMinutes';
import {EMPTY, interval, merge, type Observable, of, Subject} from 'rxjs';
import {catchError, distinctUntilChanged, filter, first, map, pairwise, switchMap, tap} from 'rxjs/operators';
import {ConnectionStatus, NetworkService} from './network.service';
import {LoggingService} from './logging.service';
import {NoticeService} from "./notice.service";

@Injectable({
  providedIn: 'root',
})
export class AppTimerService {
  public systemTimer: { value: number; source: string } = {value: null, source: null};
  private isOnline: boolean;
  private readonly restart$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly network: NetworkService,
    private readonly toast: ToastController,
    private readonly notice: NoticeService,
    private readonly logger: LoggingService,
  ) {
    merge(
      this.restart$.pipe(
        tap(() =>
          this.logger.information(
            `Clock restart called at device time: ${formatDate(Date.now(), 'mediumTime', 'en-us')}`,
            {
              loc: 'timer: 33',
              deviceTime: Date.now(),
            },
          ),
        ),
        switchMap(() =>
          this.isOnline
            ? this.getServerTime().pipe(map((time) => ({startTime: time, restarted: true})))
            : of({startTime: undefined, restarted: true}),
        ),
      ),
      this.network.onNetworkChange.pipe(
        tap((status) =>
          this.logger.information(
            `Clock network change detected; online: ${status.status === ConnectionStatus.Online}`,
            {
              loc: 'timer: 49',
              status,
            },
          ),
        ),
        tap((status) => (this.isOnline = status.status === ConnectionStatus.Online)),
        pairwise(),
        filter(([, curr]) => curr.status === ConnectionStatus.Online),
        switchMap(([prev]) =>
          this.getServerTime().pipe(
            tap((time) =>
              this.logger.information(`Server time received: ${formatDate(time, 'mediumTime', 'en-us')}`, {
                loc: 'timer: 61',
                time,
              }),
            ),
            map((time) => ({startTime: time, restarted: prev.status === ConnectionStatus.Offline})),
          ),
        ),
      ),
      this.network.onNetworkChange.pipe(
        filter((status) => status.status !== undefined),
        first(),
        filter((status) => status.status === ConnectionStatus.Offline),
        tap((status) => this.logger.information('Clock started offline', {loc: 'timer: 73', status})),
        map(() => ({startTime: undefined, restarted: false})),
      ),
    )
      .pipe(
        tap(({startTime, restarted}) =>
          this.logger.information(
            `Clock ${restarted ? 'restarted' : 'started'} at: ${formatDate(
              startTime ?? Date.now(),
              'mediumTime',
              'en-us',
            )}`,
            {
              loc: 'timer: 86',
              startTime,
              restarted,
            },
          ),
        ),
        switchMap(({startTime, restarted}) => {
          const before = performance.now();
          const dvcStart = Date.now();

          this.systemTimer.source = startTime ? 'server' : 'device';
          startTime = startTime ?? dvcStart;
          this.systemTimer.value = startTime;
          this.logger.information(
            `Clock init value: [${formatDate(this.systemTimer.value, 'mediumTime', 'en-us')}] and source: [${
              this.systemTimer.source
            }]`,
            {
              loc: 'timer: 104',
              startTime,
              before,
              dvcStart,
              restarted,
            },
          );
          if (restarted) {
            this.toast
              .create({
                message: `Clock restarted at: ${formatDate(startTime, 'mediumTime', 'en-us')}`,
                duration: 5000,
                position: 'top',
              })
              .then((toast) => toast.present());
          }

          let prevDeviceNow = dvcStart;
          let prevPerformance = before;
          let prevTime = startTime;
          return interval(1000).pipe(
            map(() => {
              const deviceNow = Date.now();
              const currPerformance = performance.now();
              const elapsed = currPerformance - before;
              const restart = Math.abs(deviceNow - dvcStart - elapsed) > 60000 || currPerformance < prevPerformance;
              const time = restart ? deviceNow : startTime + elapsed;
              if (time < prevTime - 60000) {
                this.logger.warning(`Backwards clock movement detected.`, {
                  loc: 'timer: 133',
                  time,
                  restart,
                  elapsed,
                  startTime,
                  before,
                  dvcStart,
                  prev: {
                    prevDeviceNow,
                    prevPerformance,
                    prevTime,
                  },
                  curr: {
                    dvcNow: deviceNow,
                    currPerformance,
                  },
                });
              }
              prevDeviceNow = deviceNow;
              prevPerformance = currPerformance;
              prevTime = time;
              return {time, restart, elapsed};
            }),
          );
        }),
        tap((object) => {
          this.systemTimer.value = object.time;
          // restart timer every 24 hours, if online
          if (object.elapsed > (24 * 60 * 60 * 1000) && this.isOnline) {
            this.logger.information(`Restart timer called; over 24 hrs`, {loc: 'timer: 162', object});
            this.restart$.next();
          }
        }),
        distinctUntilChanged(undefined, (object) => object.restart),
        filter((object) => object.restart),
        tap((object) => this.logger.information('Clock restart detected.', {loc: 'timer: 168', object})),
      )
      .subscribe(() => this.restart$.next());
  }

  public restart(): void {
    this.restart$.next();
  }

  private getServerTime(): Observable<number> {
    const start = Date.now();
    return this.http.get<number>(`/api/system-time`).pipe(
      map((serverTime) => {
        const deviceTime = Date.now();
        const latency = deviceTime - start;
        return ({serverTime, deviceTime, latency});
      }),
      tap(({serverTime, deviceTime, latency}) => {
        console.log('system time latency: ', latency);
        const difference = Math.abs((serverTime + latency) - deviceTime);

        if (difference > 600000) {
          const diff = this.msToTime(difference);
          this.logger.warning(
            `large time difference between server and device; difference: ${diff},
             serverTime: ${formatDate(serverTime, 'mediumTime', 'en-us')};
             deviceTime: ${formatDate(deviceTime, 'mediumTime', 'en-us')}`,
            {
              difference,
              serverTime,
              deviceTime,
              latency,
            },
          );

          this.notice.add({
            id: 0,
            label: 'Check your clock',
            title: 'Large time difference detected',
            message: `Your device time and the server time seem to be off by more then 10 minutes (${diff}).
              Please check your time and correct it as needed to ensure your punches are correct.`,
            level: 'warning',
            start: new Date(deviceTime).toISOString(),
            expiration: addMinutes(deviceTime, 60).toISOString(),
            dismissed: false,
          }).then();
        }
      }),
      map(({serverTime, latency}) => serverTime + latency),
      catchError(() => EMPTY),
    );
  }

  private msToTime(ms: number) {
    // 1. Convert to total seconds
    let s = Math.floor(ms / 1000);

    // 2. Extract hours, minutes, and remaining seconds using modulo
    const h = Math.floor(s / 3600); // 3600 seconds in 1 hour
    s %= 3600; // Remaining seconds after extracting hours

    const m = Math.floor(s / 60); // 60 seconds in 1 minute
    s %= 60; // Remaining seconds after extracting minutes

    // Function to add leading zeros to single-digit numbers for consistent formatting
    const pad = (num: number) => num.toString().padStart(2, '0');

    // Format the time as HH:MM:SS
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
}
